import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  OnInit,
  AfterViewInit,
  OnChanges,
} from '@angular/core';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { Subject, takeUntil } from 'rxjs';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { OverlayService } from '../../services/overlayService';
import { AssetListSortType, AssetListSortTypes } from '../../models';
import { AssetExportService } from '../../services/asset-export.service';
import { AssetListService } from '../../services/asset-list.service';
import { DateHelperService } from '../../services/date-helper.service';
import { MtmEditAssetModalComponent } from '../asset-list/components/edit-asset-modal/edit-asset-modal.component';
import { MoveToModalComponent } from '../move-to-modal/move-to-modal.component';
import { AssetModelComponent } from '../asset-model/asset-model.component';
import { AssetPropertiesAvailabilityService } from '../asset-list/services/asset-properties-availability.service';

@Component({
  selector: 'mtm-assets-table-view',
  templateUrl: './assets-table-view.component.html',
  styleUrl: './assets-table-view.component.scss',
  standalone: false,
})
export class AssetsTableViewComponent implements OnDestroy, OnInit, AfterViewInit, OnChanges {
  @ViewChildren('dropdownRef') dropdowns!: QueryList<NgbDropdown>;
  @Input() bundle: any;
  @Input() originalBundle: any;
  @Input() currentSort: AssetListSortTypes = AssetListSortType.releaseDate;
  @Input() campaignId: string;
  @Input() projectId: string;
  @Input() asWidget: boolean = false;
  @Input() isViewMode: boolean = false;
  @Input() forSelection: boolean = false;
  @Output() assetsUpdated: EventEmitter<any> = new EventEmitter();
  @Output() assetUpdated: EventEmitter<any> = new EventEmitter();
  @Output() assetDeleted: EventEmitter<any> = new EventEmitter();
  @Output() assetDeletedSubAsset: EventEmitter<any> = new EventEmitter();
  @Output() bulkUpdateAssets: EventEmitter<any> = new EventEmitter();
  @Output() dragAndDropAssets: EventEmitter<any> = new EventEmitter();
  @Output() assetBundleChange: EventEmitter<any> = new EventEmitter();
  @Output() rowSelectEvent: EventEmitter<any> = new EventEmitter();

  selectedIds: string[] = [];
  selectedTechnicalVariants: string[] = [];
  expandedItemId = null;
  affectedAssets: any = {};
  ngUnsubscribe = new Subject();
  canDeleteSelectedAssets: boolean = false;
  areAllSelectedHidden: boolean = false;
  draggedCard: any = null;
  targetCardId: any = null;
  isInMoveToSelectOptions = false;
  selectedRow: any = null;
  regions: string[] = [];
  agencys: string[] = [];
  focuses: string[] = [];
  types: string[] = [];
  categories: string[] = [];

  constructor(
    private readonly assetListService: AssetListService,
    private readonly modalService: NgbModal,
    private readonly assetExportService: AssetExportService,
    private readonly translatePipe: TranslatePipe,
    private readonly overlayService: OverlayService,
    private readonly authService: AuthService,
    private readonly dateHelperService: DateHelperService,
    private readonly assetPropertiesAvailabilityService: AssetPropertiesAvailabilityService
  ) {}

  get allTechnicalVariants() {
    return this.bundle
      .filter((asset) => asset?.subAsset?.length)
      .map((asset) => {
        return asset.subAsset.map((tv) => ({ ...tv, masterId: asset.id }));
      })
      .flat();
  }

  get allOptionIcons() {
    if (
      this.selectedIds.length === this.bundle.length &&
      this.selectedTechnicalVariants.length === this.allTechnicalVariants.length
    ) {
      return 'assets/svg/selected.svg';
    }
    return this.selectedIds?.length || this.selectedTechnicalVariants?.length
      ? 'assets/svg/filtered.svg'
      : 'assets/svg/unselect.svg';
  }

  ngOnInit(): void {
    this.loadFeatureConfigs();
    this.getLinkedAssets();
  }

  loadFeatureConfigs(): void {
    this.assetPropertiesAvailabilityService
      .getAvailablePropertiesFromApi()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (properties) => {
          this.regions = properties.region;
          this.agencys = properties.agency;
          this.focuses = properties.focus;
          this.types = properties.type;
          this.categories = properties.category;
        },
      });
  }

  ngAfterViewInit() {
    window.addEventListener('scroll', this.closeDropdownsOnScroll, true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bundle?.currentValue) {
      this.sortAssetBundle();
    }
    if (changes.currentSort?.currentValue) {
      this.sortAssetBundle();
    }
    if (this.campaignId) {
      this.getLinkedAssets();
    }
  }

  closeDropdownsOnScroll = () => {
    this.dropdowns.forEach((dropdown) => {
      if (dropdown.isOpen() && !this.isInMoveToSelectOptions) {
        dropdown.close();
      }
    });
  };

  onRowSelect(item: any) {
    if (!this.forSelection) {
      return;
    }
    if (item.id === this.selectedRow?.id) {
      this.selectedRow = null;
    } else if (item.id !== this.selectedRow?.id) {
      this.selectedRow = item;
    }
    this.rowSelectEvent.emit(this.selectedRow);
  }

  onDragStart(card: any) {
    this.draggedCard = card;
  }

  onDragOver(event: DragEvent, targetCardId: any) {
    event.preventDefault();
    this.targetCardId = targetCardId;
  }

  onDragLeave() {
    this.targetCardId = null;
  }

  onDragEnd() {
    this.draggedCard = null;
    this.targetCardId = null;
  }

  onMoveToModalOpen(source: any) {
    this.draggedCard = source;
    const assets = this.bundle.filter((a) => a.id !== source.id && !a.hidden);
    const moveToModal = this.modalService.open(MoveToModalComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    moveToModal.componentInstance.source = source;
    moveToModal.componentInstance.assets = assets;
    moveToModal.componentInstance.campaignId = this.campaignId;
    moveToModal.result.then((target) => {
      if (target?.id) {
        this.onDrop(target, false);
      } else {
        this.draggedCard = null;
      }
    });
  }

  onDrop(targetCard: any, showConfirmDialog = true) {
    const dragAsset = this.draggedCard;
    const dropAsset = targetCard;

    if (!dragAsset || !dropAsset) {
      this.draggedCard = null;
      this.targetCardId = null;
      return;
    }

    if (dragAsset.id === dropAsset.id) {
      this.draggedCard = null;
      this.targetCardId = null;
      return;
    }

    if (dragAsset && this.affectedAssets[dragAsset.id]) {
      this.overlayService.showError('This Master asset is already assigned to a project and cannot be moved!');
      this.draggedCard = null;
      this.targetCardId = null;
      return;
    }

    if (showConfirmDialog) {
      const modalRef = this.modalService.open(AssetModelComponent, {
        backdrop: 'static',
        keyboard: false,
      });

      modalRef.componentInstance.title = this.translatePipe.transform('asset_mergeAsset') || 'Merge Asset';

      const master = dragAsset.detail || dragAsset.title || 'Master asset';
      const target = dropAsset.detail || dropAsset.title || 'Target asset';

      modalRef.componentInstance.description = `You're about to convert <b>"${master}"</b> into a Technical Variant of  <b>"${target}"</b>.<br><br> This action will change it's status and link it under the selected Master.`;

      modalRef.result
        .then((confirm: any) => {
          if (!confirm) {
            this.draggedCard = null;
            this.targetCardId = null;
            return;
          }
          this.doMoveAsset(dragAsset, dropAsset);
        })
        .catch(() => {
          this.draggedCard = null;
          this.targetCardId = null;
        });
    } else {
      this.doMoveAsset(dragAsset, dropAsset);
    }
  }

  doMoveAsset(dragAsset: any, dropAsset: any) {
    const dragSubAsset = [
      ...(dragAsset?.subAsset || []),
      {
        id: dragAsset.id,
        funnel: dropAsset.funnel,
        type: dragAsset.type,
        detail: dragAsset.detail,
        focus: dragAsset.focus,
        categories: dragAsset.categories,
        bets: dragAsset.bets,
        length: dragAsset.length,
        format: dragAsset.format,
        assetType: 'TECHNICAL_VARIANT',
        initial: dropAsset.initial,
      },
    ];

    this.bundle = this.bundle.filter((asset) => asset.id !== dragAsset?.id);

    this.bundle = this.bundle.map((asset) => {
      if (asset.id === dropAsset.id) {
        return {
          ...asset,
          subAsset: [...(Array.isArray(asset.subAsset) ? asset.subAsset : []), ...dragSubAsset],
        };
      }
      return asset;
    });

    this.dragAndDropAssets.emit({
      dragAssetId: dragAsset.id,
      dropAssetId: dropAsset.id,
    });
    this.overlayService.showSuccess('Asset successfully moved as a Technical Variant!');
    this.draggedCard = null;
    this.targetCardId = null;
    this.assetBundleChange.emit(this.bundle);
  }

  getLinkedAssets() {
    if (this.isViewMode) {
      return;
    }
    this.assetListService
      .getAffectedAssetByCampaignId(this.campaignId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          this.affectedAssets = {};
          for (let i = 0; i < res.length; i++) {
            this.affectedAssets[res[i].assetListId] = res[i];
            if (this.affectedAssets[res[i].assetListId].deliveryDate) {
              this.affectedAssets[res[i].assetListId].deliveryDate = this.dateHelperService.fromDateToDatepicker(
                new Date(this.affectedAssets[res[i].assetListId].deliveryDate)
              );
            }
          }
        },
      });
  }

  sortAssetBundle() {
    if (!this.bundle?.length) return;

    switch (this.currentSort) {
      case AssetListSortType.releaseDate:
        this.bundle.sort((a, b) =>
          (b.releaseDate?.year
            ? new Date(b.releaseDate.year, b.releaseDate.month - 1, b.releaseDate.day).getTime()
            : 0) >
          (a.releaseDate?.year ? new Date(a.releaseDate.year, a.releaseDate.month - 1, a.releaseDate.day).getTime() : 0)
            ? 1
            : -1
        );
        break;
      case AssetListSortType.status:
        this.bundle.sort((a, b) =>
          (this.affectedAssets[a.id]?.status || '') > (this.affectedAssets[b.id]?.status || '') ? 1 : -1
        );
        break;
      case AssetListSortType.lastUpdated:
        this.bundle.sort((a, b) =>
          (b.updatedAt ? new Date(b.updatedAt).getTime() : 0) > (a.updatedAt ? new Date(a.updatedAt).getTime() : 0)
            ? 1
            : -1
        );
        break;
      case AssetListSortType.alphabetical:
        this.bundle.sort((a, b) => ((a.title || a.detail || '') > (b.title || b.detail || '') ? 1 : -1));
        break;
      default:
        break;
    }
  }

  getItemIcon(item: any) {
    if (this.selectedIds.includes(item.id)) {
      return 'assets/svg/selected.svg';
    }
    if (item?.subAsset?.length) {
      const generatedTechnicalVariantUniqueFields = item.subAsset.map((v) => `${item.id}_${v.id}`);
      if (
        generatedTechnicalVariantUniqueFields.some((uniqueField) =>
          this.selectedTechnicalVariants.includes(uniqueField)
        )
      ) {
        return 'assets/svg/dash-selected.svg';
      }
      return 'assets/svg/unselect.svg';
    }
    return 'assets/svg/unselect.svg';
  }

  getTechnicalVariantIcon(item: any, variant: any) {
    const uniqueField = `${item.id}_${variant.id}`;
    if (this.selectedTechnicalVariants.includes(uniqueField)) {
      return 'assets/svg/selected.svg';
    }
    return 'assets/svg/unselect.svg';
  }

  onExpand(item: any, $event: Event) {
    $event.stopPropagation();
    if (item?.subAsset.length) {
      if (item.id === this.expandedItemId) {
        this.expandedItemId = null;
      } else {
        this.expandedItemId = item.id;
      }
    }
  }

  onItemSelect(item: any) {
    if (this.selectedIds.includes(item.id)) {
      this.selectedIds = [...this.selectedIds.filter((id) => id !== item.id)];
      if (item?.subAsset?.length) {
        const generatedTechnicalVariantUniqueFields = item.subAsset.map((v) => `${item.id}_${v.id}`);
        this.selectedTechnicalVariants = [
          ...this.selectedTechnicalVariants.filter((stv) => !generatedTechnicalVariantUniqueFields.includes(stv)),
        ];
      }
    } else {
      this.selectedIds = [...this.selectedIds, item.id];
      if (item?.subAsset?.length) {
        const generatedTechnicalVariantUniqueFields = item.subAsset.map((v) => `${item.id}_${v.id}`);
        this.selectedTechnicalVariants = [...this.selectedTechnicalVariants, ...generatedTechnicalVariantUniqueFields];
      }
    }
    this.updateExportAssets();

    this.canDeleteSelectedAssets = this.canDeleteAllSelected();
  }

  onTechnicalVariantSelect(item: any, variant: any) {
    const uniqueField = `${item.id}_${variant.id}`;
    if (this.selectedTechnicalVariants.includes(uniqueField)) {
      this.selectedTechnicalVariants = [...this.selectedTechnicalVariants.filter((id) => id !== uniqueField)];
      this.selectedIds = [...this.selectedIds.filter((id) => id !== item.id)];
    } else {
      this.selectedTechnicalVariants = [...this.selectedTechnicalVariants, uniqueField];
      const generatedTechnicalVariantUniqueFields = item.subAsset.map((v) => `${item.id}_${v.id}`);
      const hasAllSelected = generatedTechnicalVariantUniqueFields.every((uniqueField) =>
        this.selectedTechnicalVariants.includes(uniqueField)
      );
      if (hasAllSelected) {
        if (!this.selectedIds.includes(item.id)) {
          this.selectedIds = [...this.selectedIds, item.id];
        }
      } else {
        this.selectedIds = [...this.selectedIds.filter((id) => id !== item.id)];
      }
    }
    this.updateExportAssets();
  }

  onAllOptionsToggle() {
    if (
      this.selectedIds.length === this.bundle.length &&
      this.selectedTechnicalVariants.length === this.allTechnicalVariants.length
    ) {
      this.selectedIds = [];
      this.selectedTechnicalVariants = [];
    } else {
      this.selectedIds = [...this.bundle.map((asset) => asset.id)];
      this.selectedTechnicalVariants = this.allTechnicalVariants.map((item) => {
        return `${item.masterId}_${item.id}`;
      });
    }
    this.updateExportAssets();
  }

  private updateExportAssets() {
    const selectedAssets = this.bundle.filter((asset) => this.selectedIds.includes(asset.id));
    const assetsToExport = [
      ...selectedAssets.map((asset) => {
        const technicalVariants = asset.subAsset.filter((variant) =>
          this.selectedTechnicalVariants.includes(`${asset.id}_${variant.id}`)
        );

        return {
          ...asset,
          isVariant: false,
          subAsset: technicalVariants,
        };
      }),
    ];

    this.assetExportService.updateAssetsToExport(assetsToExport);
  }

  openEditAssetModal(funnel: string, item: any) {
    if (!this.originalBundle?.length) {
      return;
    }
    const editModal = this.modalService.open(MtmEditAssetModalComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    editModal.componentInstance.asset = { ...item };
    editModal.componentInstance.funnel = funnel;
    editModal.result.then((result: any) => {
      if (!result) {
        return;
      }
      delete result?.affectedAsset;
      this.assetUpdated.emit(result);
    });
  }

  duplicateAsset(content: any) {
    if (!this.originalBundle?.length) {
      return;
    }

    const result = {
      funnel: content.funnel,
      focus: content.focus,
      type: content.type,
      detail: `Copy of ${content.detail || content.title}`,
      bets: [...(content.bets || [])],
      categories: [...(content.categories || [])],
      companyId: content.companyId,
      format: [...(content.format || [])],
      length: [...(content.length || [])],
      subAsset: (content.subAsset || []).map((subAsset) => {
        const { id, ...subAssetWithoutId } = subAsset;
        return subAssetWithoutId;
      }),
      createdBy: this.authService.getAuthUser().email,
      initial: false,
      assetType: 'MASTER',
    };

    this.bulkUpdateAssets.emit({
      action: 'DUPLICATE',
      assetIds: [content.id],
    });
  }

  duplicateVariant(item: any, variant: any) {
    if (!this.originalBundle?.length) {
      return;
    }
    const originalTitle = variant.title || variant.detail || '';
    const baseTitle = originalTitle.replace(/\s*\(\d+\)\s*$/, '');
    let maxNum = 0;
    item.subAsset.forEach((v) => {
      if ((v.detail || v.title).startsWith(baseTitle)) {
        const match = (v.detail || v.title).match(/\((\d+)\)/);
        if (match) {
          const num = parseInt(match[1], 10);
          maxNum = Math.max(maxNum, num);
        }
      }
    });
    const nextNum = ++maxNum;
    const res = {
      ...item,
      subAsset: [
        ...(item?.subAsset || []),
        {
          ...variant,
          title: `${baseTitle} (${nextNum})`,
          detail: `${baseTitle} (${nextNum})`,
          id: uuid4(),
        },
      ],
    };
    this.assetUpdated.emit(res);
  }

  deleteAsset(asset: any): void {
    const modalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = this.translatePipe.transform('asset_deleteAsset') || 'Delete Asset';
    modalRef.componentInstance.description = `Are you sure you want to delete the asset <strong>"${asset.detail}"</strong>?`;

    modalRef.result
      .then((confirm: any) => {
        if (!confirm || !this.originalBundle?.length) return;

        this.originalBundle = this.originalBundle.filter((a) => a.id !== asset.id);
        this.bundle = this.bundle.filter((a) => a.id !== asset.id);

        this.assetDeleted.emit(asset);
        this.assetBundleChange.emit(this.bundle);

        this.overlayService.showSuccess(this.translatePipe.transform('asset delete Successfully'));
      })
      .catch(() => {
        // Modal dismissed, no action needed
      });
  }

  deleteVariant(item: any, variant: any): void {
    const modalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = this.translatePipe.transform('asset_deleteAsset') || 'Delete Variant';
    modalRef.componentInstance.description = `Are you sure you want to delete the variant <strong>"${variant.detail}"</strong>?`;

    modalRef.result
      .then((confirm: any) => {
        if (!confirm || !this.originalBundle?.length) return;

        // Update originalBundle
        this.originalBundle = this.originalBundle.map((a) => {
          if (a.id === item.id) {
            return { ...a, subAsset: a.subAsset.filter((v) => v.id !== variant.id) };
          }
          return a;
        });

        // Update bundle
        this.bundle = this.bundle.map((a) => {
          if (a.id === item.id) {
            return { ...a, subAsset: a.subAsset.filter((v) => v.id !== variant.id) };
          }
          return a;
        });

        this.assetBundleChange.emit(this.bundle);
        this.assetDeletedSubAsset.emit({ id: item?.id, subAssetId: variant?.id });
      })
      .catch(() => {
        // Modal dismissed, do nothing
      });
  }

  hideAsset(asset: any) {
    asset.hidden = !asset.hidden;
    this.assetUpdated.emit(asset);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  toggleHideSelectedAssets() {
    if (!this.selectedIds || this.selectedIds.length === 0) return;

    // Get all selected assets
    const selectedAssets = this.bundle.filter((asset) => this.selectedIds.includes(asset.id));

    // Determine if all selected assets are currently hidden
    const allHidden = selectedAssets.every((asset) => asset.hidden === true);

    // Determine action type - UNHIDE if they're all currently hidden, otherwise HIDE
    const action = allHidden ? 'UNHIDE' : 'HIDE';

    // Update local bundle to maintain component state
    const bundleCopy = this.bundle.map((asset) => {
      if (this.selectedIds.includes(asset.id)) {
        return { ...asset, hidden: !allHidden };
      }
      return { ...asset };
    });

    this.bundle = bundleCopy;

    // Emit the updated data in the new format
    this.bulkUpdateAssets.emit({
      action: action,
      assetIds: this.selectedIds,
    });

    this.areAllSelectedHidden = !allHidden;
  }

  isCreator(createdBy: string): boolean {
    const authUser = this.authService.getAuthUser();
    return authUser?.email === createdBy;
  }

  canDeleteAllSelected(): boolean {
    const selectedAssets = this.bundle.filter((asset) => this.selectedIds.includes(asset.id));
    const hasAffectedAssets = selectedAssets.some((asset) => this.affectedAssets[asset.id]);
    if (hasAffectedAssets) return false;
    return selectedAssets.every((asset) => asset.initial === false && this.isCreator(asset.createdBy));
  }

  deleteSelectedAssets(): void {
    const selectedAssets = this.bundle.filter((asset) => this.selectedIds.includes(asset.id));
    if (selectedAssets.length === 0) return;

    const modalRef = this.modalService.open(AssetModelComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = this.translatePipe.transform('asset_deleteAsset') || 'Delete Assets';
    modalRef.componentInstance.description =
      modalRef.componentInstance.description = `Are you sure you want to delete these <strong>"${selectedAssets.length}"</strong> selected assets?`;

    modalRef.result
      .then((confirm: any) => {
        if (!confirm) return;

        // Filter out selected assets from bundle
        this.bundle = this.bundle.filter((asset) => !this.selectedIds.includes(asset.id));

        const idsToDelete = selectedAssets.map((asset) => asset.id);

        this.bulkUpdateAssets.emit({
          action: 'DELETE',
          assetIds: idsToDelete,
        });

        this.assetBundleChange.emit(this.bundle);
      })
      .catch(() => {
        // Modal dismissed, do nothing
      });
  }

  duplicateSelectedAssets() {
    const selectedAssets = this.bundle.filter((asset) => this.selectedIds.includes(asset.id));

    const copyassetArray = [];

    for (const content of selectedAssets) {
      const result = {
        id: uuid4(),
        assetBundle: this.bundle,
        funnel: content.funnel,
        focus: content.focus,
        type: content.type,
        detail: content.detail,
        bets: [...(content.bets || [])],
        categories: [...(content.categories || [])],
        companyId: content.companyId,
        format: [...(content.format || [])],
        length: [...(content.length || [])],
        subAsset: [...(content.subAsset || [])],
      };
      copyassetArray.push(result);
    }

    const idsToDelete = selectedAssets.map((asset) => asset.id);
    this.bulkUpdateAssets.emit({
      action: 'DUPLICATE',
      assetIds: idsToDelete,
    });
  }

  convertToDateObject(timestamp: any): any {
    if (!timestamp) return '';

    if (typeof timestamp === 'object' && 'year' in timestamp && 'month' in timestamp && 'day' in timestamp) {
      return timestamp;
    }

    const date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) return '';

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  formatNgbDate(date: any): string {
    if (!date) return '';

    const day = date.day.toString().padStart(2, '0');
    const month = date.month.toString().padStart(2, '0');
    const { year } = date;

    return `${day}.${month}.${year}`;
  }
}
